import { CdkMenuTrigger } from '@angular/cdk/menu';
import { Component, DestroyRef, ElementRef, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SiEmptyStateModule } from '@siemens/element-ng/empty-state';
import { SiLoadingSpinnerComponent } from '@siemens/element-ng/loading-spinner';
import { SiMenuModule } from '@siemens/element-ng/menu';
import { SiToastNotificationService } from '@siemens/element-ng/toast-notification';
import * as XLSX from 'xlsx';

import { environment } from '../../../environments/environment';
import { Schedule } from '../shared/DataTypes';
import { AccountService } from '../shared/services/account.service';
import { EventService } from '../shared/services/event.service';
import { ShareDataService } from '../shared/services/share-data.service';
import { SimulationService } from '../shared/services/simulation.service';
import { InfosComponent } from './infos/infos.component';
import { PlotsComponent } from './plots/plots.component';
import { captureVisibleCharts } from './pptx/chart-to-png';
import { buildPptxLabels, buildPptxPayload } from './pptx/payload-builder';
import { PptxExportService } from './pptx/pptx-export.service';
import { ResultsService } from './results.service';
import { TablesComponent } from './tables/tables.component';
import { UserchartsComponent } from './usercharts/usercharts.component';

type ResultsExportData = Record<string, unknown>;

@Component({
  selector: 'app-results',
  imports: [
    PlotsComponent,
    TablesComponent,
    InfosComponent,
    UserchartsComponent,
    SiEmptyStateModule,
    SiLoadingSpinnerComponent,
    TranslateModule,
    CdkMenuTrigger,
    SiMenuModule
  ],
  templateUrl: './results.component.html'
})
export class ResultsComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly accountService = inject(AccountService);
  private readonly elementRef = inject(ElementRef);
  private readonly shareDataService = inject(ShareDataService);
  private readonly pptxExportService = inject(PptxExportService);
  private readonly simulationService = inject(SimulationService);
  eventService = inject(EventService);
  resultService = inject(ResultsService);
  translateService = inject(TranslateService);
  toastNotificationService = inject(SiToastNotificationService);

  readonly results = signal<ResultsExportData | null>(null);
  readonly simulationRunning = signal<boolean>(false);
  readonly simulatingSchedule = signal<Schedule | null>(null);
  readonly distinctTrackIdsInSimulatedSchedule = signal<number[]>([]);
  readonly pptxExporting = signal<boolean>(false);
  readonly canExportPptx = computed(() => this.accountService.hasDetailedSimulationResultsAccess());
  readonly chooseSimulationKernel = computed(
    () => this.shareDataService.runtimeConfig().chooseSimulationKernel
  );
  readonly kernelTimeMs = computed(
    () => (this.results() as any)?.kernel_time_ms as number | undefined
  );
  readonly totalTimeMs = computed(
    () => (this.results() as any)?.total_time_ms as number | undefined
  );

  constructor() {
    if (this.eventService.getLastSimulationResults() === null && !environment.production) {
      this.preloadMockResultsForDev();
    }

    this.eventService.simulatingScheduleChangedEvent$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((simulatingSchedule: Schedule | null) => {
        if (simulatingSchedule === null) {
          this.simulationRunning.set(false);
          return;
        }

        this.simulatingSchedule.set(simulatingSchedule);
        this.simulationRunning.set(true);
      });

    if (this.eventService.getLastSimulationResults() !== null) {
      this.results.set(this.eventService.getLastSimulationResults());
    }

    this.eventService.simulationResultsChangedEvent$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((results: ResultsExportData | null) => {
        this.results.set(results);
      });
  }

  private async preloadMockResultsForDev(): Promise<void> {
    try {
      const { mockResults } = await import('../shared/mockdata/mock-results');
      if (this.eventService.getLastSimulationResults() === null) {
        this.eventService.updateSimulationResults(mockResults as ResultsExportData);
      }
    } catch {
      // Keep normal dev flow when mock data is unavailable.
      console.warn('Mock results data not available for preloading in development mode.');
    }
  }

  /**
   * Handles export errors with toast notification
   */
  private handleExportError(e: Error | string) {
    this.toastNotificationService.showToastNotification({
      state: 'danger',
      title: this.translateService.instant('SHARED.ERROR_EXPORT', {
        type: this.translateService.instant('TRACK.TRACK')
      }),
      message: typeof e === 'string' ? e : e.message
    });
  }

  private getResultsForExport(): ResultsExportData | null {
    const results = this.results();

    if (!results) {
      this.handleExportError(this.translateService.instant('RESULTS.INFO_SIMULATE_SCHEDULE'));
      return null;
    }

    return results;
  }

  private getExportData(results: ResultsExportData): ResultsExportData | Record<string, unknown>[] {
    const timepointResultTable = results['timepoint_result_table'];
    if (Array.isArray(timepointResultTable) && timepointResultTable.length > 0) {
      return timepointResultTable;
    }

    const resultTable = results['result_table'];
    if (Array.isArray(resultTable) && resultTable.length > 0) {
      return resultTable;
    }

    return results;
  }

  getScheduleName(results: ResultsExportData): string {
    const energyConsumptionResults = results['energy_consumption_results'];
    if (!energyConsumptionResults || typeof energyConsumptionResults !== 'object') {
      return '';
    }

    const scheduleName = (energyConsumptionResults as Record<string, unknown>)['timetable_name'];
    return typeof scheduleName === 'string' ? scheduleName : '';
  }

  private sanitizeFileName(value: string): string {
    return Array.from(value.trim())
      .filter(character => character.charCodeAt(0) >= 32)
      .join('')
      .replace(/[<>:"/\\|?*]/g, '_')
      .replace(/[. ]+$/g, '');
  }

  private getExportFileBaseName(results: ResultsExportData): string {
    const sanitizedScheduleName = this.sanitizeFileName(this.getScheduleName(results));

    if (sanitizedScheduleName.length > 0) {
      return sanitizedScheduleName;
    }

    return 'results';
  }

  /**
   * Fetches and converts track/vehicle data for export
   * Returns an observable that emits the converted data
   */
  private fetchExportData() {
    const results = this.getResultsForExport();
    if (!results) {
      return null;
    }

    const schedule = this.eventService.getLastSimulatedSchedule();
    // The backend re-merges these onto the route-wide position axis, so it needs the
    // whole track list rather than just their ids.
    const tracks = this.simulationService.mapScheduleTracksToEngineTracks(schedule?.tracks ?? []);

    if (tracks.length === 0) {
      this.toastNotificationService.showToastNotification({
        state: 'danger',
        title: this.translateService.instant('SHARED.ERROR_EXPORT', {
          type: this.translateService.instant('TRACK.TRACK')
        }),
        message: 'No track data available for export processing.'
      });
      return null;
    }

    return this.resultService.convertTrackAndVehicleData(tracks, results);
  }

  private downloadFile(blob: Blob, fileName: string) {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    this.simulateLinkClick(link);
    URL.revokeObjectURL(url);
  }

  exportResultsAsTable() {
    const results = this.getResultsForExport();
    if (!results) return;

    const exportData = this.getExportData(results);
    const jsonString = JSON.stringify(exportData, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    this.downloadFile(blob, `${this.getExportFileBaseName(results)}.json`);
  }

  exportResultsAsExcel() {
    const results = this.getResultsForExport();
    if (!results) return;

    const exportData$ = this.fetchExportData();
    if (!exportData$) return;

    exportData$.subscribe({
      next: (response: { data?: { df_data_export?: string } }) => {
        try {
          const rawExportData = response.data?.df_data_export;
          if (!rawExportData) {
            this.handleExportError('Export data is empty');
            return;
          }

          const exportData = this.getExportData(results);
          const jsonData = JSON.parse(rawExportData);
          const exportRows = Array.isArray(jsonData) ? jsonData : [jsonData];
          const worksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(exportRows);
          const workbook: XLSX.WorkBook = XLSX.utils.book_new();
          XLSX.utils.book_append_sheet(workbook, worksheet, 'ExportedData');
          const resultRows = Array.isArray(exportData) ? exportData : [exportData];
          const worksheetResults: XLSX.WorkSheet = XLSX.utils.json_to_sheet(resultRows);
          XLSX.utils.book_append_sheet(workbook, worksheetResults, 'ResultsData');
          const excelBuffer: BlobPart = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
          const blob: Blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
          this.downloadFile(blob, `${this.getExportFileBaseName(results)}.xlsx`);
        } catch (error) {
          this.handleExportError(error instanceof Error ? error : 'Failed to export excel file');
        }
      },
      error: (e: Error) => this.handleExportError(e)
    });
  }

  async exportResultsAsPptx() {
    const results = this.getResultsForExport();
    if (!results || this.pptxExporting()) return;

    this.pptxExporting.set(true);
    try {
      const charts = await captureVisibleCharts(this.elementRef.nativeElement as HTMLElement);
      const labels = buildPptxLabels(key => String(this.translateService.instant(key)));
      const payload = buildPptxPayload({
        results,
        schedule: this.eventService.getLastSimulatedSchedule(),
        vehicle: this.eventService.getSelectedVehicle(),
        vehicleSimulationData: this.shareDataService.getVehicleSimulationData(),
        charts,
        labels,
        locale: this.translateService.getCurrentLang(),
        author: this.accountService.accountSummary()?.gid,
        appVersion: this.shareDataService.getBuildVersion()
      });

      this.pptxExportService.exportPptx(payload).subscribe({
        next: (blob: Blob) => {
          this.downloadFile(blob, `${this.getExportFileBaseName(results)}.pptx`);
          this.pptxExporting.set(false);
        },
        error: (e: Error) => {
          this.handleExportError(e);
          this.pptxExporting.set(false);
        }
      });
    } catch (error) {
      this.handleExportError(error instanceof Error ? error : 'Failed to export PPTX');
      this.pptxExporting.set(false);
    }
  }

  simulateLinkClick(link: HTMLAnchorElement) {
    // Append the link to the document body
    document.body.appendChild(link);
    // Trigger the click event on the link to initiate download
    link.click();
    // Clean up: remove the link and revoke the URL
    document.body.removeChild(link);
  }
}
